import { useState } from 'react'

import {
  Box,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material'
import {
  IconBrandSlack,
  IconCircleDashedCheck,
  IconColumns2,
  IconUser,
  IconCreditCard,
  IconTrophy,
  IconPhoto,
  IconMessage,
  IconHome,
  IconSparkles,
} from '@tabler/icons-react'

import { Link, navigate } from '@redwoodjs/router'
import { Toaster } from '@redwoodjs/web/toast'

import { useAuth } from 'src/auth'
import { AvatarCacheProvider } from 'src/components/AvatarCacheProvider/AvatarCacheProvider'
import WorkspaceFinder from 'src/components/WorkspaceFinder/WorkspaceFinder'
import { internalUserEmails } from 'src/lib/gates'

type AdminLayoutProps = {
  children?: React.ReactNode
}

const drawerWidth = 240

const getAdminNavItems = (setOrgEnrichModalOpen: (open: boolean) => void) => [
  {
    icon: <IconUser size={20} />,
    label: 'Users',
    to: '/admin',
  },
  {
    icon: <IconCreditCard size={20} />,
    label: 'Subscriptions',
    to: '/admin/subscriptions',
  },
  {
    icon: <IconTrophy size={20} />,
    label: 'Tiers',
    to: '/admin/tiers',
  },
  {
    icon: <IconCircleDashedCheck size={20} />,
    label: 'Data Feedback',
    to: '/admin/data/feedback',
  },
  {
    icon: <IconColumns2 size={20} />,
    label: 'Pipeline Setup',
    to: '/admin/pipelines/setup',
  },
  {
    icon: <IconBrandSlack size={20} />,
    label: 'Slack (All)',
    to: '/admin/slack',
  },
  {
    icon: <IconPhoto size={20} />,
    label: 'Org Photos',
    to: '/admin/orgs/global/photos',
  },
  {
    icon: <IconMessage size={20} />,
    label: 'Threads',
    to: '/admin/threads',
  },
  {
    icon: <IconSparkles size={20} />,
    label: 'Org Enrich',
    onClick: () => setOrgEnrichModalOpen(true),
  },
]

const containerSx = {
  display: 'flex',
  height: '100vh',
  width: '100%',
  bgcolor: 'background.default',
}

const drawerSx = {
  width: drawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    bgcolor: 'background.paper',
    borderRight: 1,
    borderColor: 'divider',
  },
}

const mainSx = {
  flexGrow: 1,
  bgcolor: 'background.default',
  height: '100vh',
  overflow: 'auto',
}

const listItemButtonSx = {
  borderRadius: 1,
  mx: 1,
  '&:hover': {
    bgcolor: 'action.hover',
  },
  '&.active': {
    bgcolor: 'action.selected',
  },
}

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const { currentUser } = useAuth()
  const [orgEnrichModalOpen, setOrgEnrichModalOpen] = useState(false)
  const [domainInput, setDomainInput] = useState('')

  // Check if user has admin access
  const hasAdminAccess = internalUserEmails.includes(currentUser?.email)

  if (!hasAdminAccess) {
    return (
      <Box sx={mainSx}>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography
            variant="h4"
            gutterBottom
          >
            Access Denied
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
          >
            You do not have permission to access the admin area.{' '}
          </Typography>
          <Box
            sx={{
              backgroundColor: 'background.paper',
              p: 2,
              borderRadius: 1,
            }}
          >
            Current: {currentUser?.email}
          </Box>
          <Box
            sx={{
              backgroundColor: 'background.paper',
              p: 2,
              borderRadius: 1,
            }}
          >
            Includes: {internalUserEmails.includes(currentUser?.email)}
          </Box>
          <Box
            sx={{
              backgroundColor: 'background.paper',
              p: 2,
              borderRadius: 1,
            }}
          >
            All: {internalUserEmails}
          </Box>
        </Box>
      </Box>
    )
  }

  const handleOrgEnrichSubmit = () => {
    // Simple regex to remove protocol if present
    const cleanDomain = domainInput
      .trim()
      .replace(/^https?:\/\//, '')
      .replace(/\/$/, '')

    if (cleanDomain) {
      setOrgEnrichModalOpen(false)
      setDomainInput('')
      navigate(`/admin/organizations/${cleanDomain}/enrich`)
    }
  }

  const handleOrgEnrichClose = () => {
    setOrgEnrichModalOpen(false)
    setDomainInput('')
  }

  const adminNavItems = getAdminNavItems(setOrgEnrichModalOpen)

  const drawer = (
    <>
      <Box sx={{ p: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: 'primary.main' }}
        >
          Day.ai Admin
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
        >
          Global Administration
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        <WorkspaceFinder />
      </Box>
      <Divider />
      <List sx={{ px: 1, py: 2 }}>
        {adminNavItems.map((item) => (
          <ListItem
            key={item.label}
            disablePadding
            sx={{ mb: 0.5 }}
          >
            <ListItemButton
              component={item.onClick ? undefined : Link}
              to={item.onClick ? undefined : item.to}
              onClick={item.onClick}
              sx={listItemButtonSx}
              className={
                window.location.pathname === item.to ? 'active' : undefined
              }
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: 500,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List sx={{ px: 1, py: 2 }}>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/home"
            sx={listItemButtonSx}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <IconHome size={20} />
            </ListItemIcon>
            <ListItemText
              primary="Back to App"
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: 500,
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
      <Box sx={{ mt: 'auto', p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography
          variant="caption"
          color="text.secondary"
        >
          Logged in as
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500 }}
        >
          {currentUser?.email}
        </Typography>
      </Box>
    </>
  )

  return (
    <AvatarCacheProvider>
      <Box sx={containerSx}>
        <Toaster toastOptions={{ className: 'rw-toast', duration: 6000 }} />
        <Drawer
          variant="permanent"
          sx={{
            ...drawerSx,
            '& .MuiDrawer-paper': {
              ...drawerSx['& .MuiDrawer-paper'],
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Box
          component="main"
          sx={mainSx}
        >
          {children}
        </Box>

        {/* Org Enrich Modal */}
        <Dialog
          open={orgEnrichModalOpen}
          onClose={handleOrgEnrichClose}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Organization Enrichment</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Domain"
              placeholder="Enter domain (e.g., example.com)"
              fullWidth
              variant="outlined"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleOrgEnrichSubmit()
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleOrgEnrichClose}>Cancel</Button>
            <Button
              onClick={handleOrgEnrichSubmit}
              variant="contained"
              disabled={!domainInput.trim()}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AvatarCacheProvider>
  )
}

export default AdminLayout
